import {userStore} from "stores";
import ConfigService from "service/ConfigService";
import CookieService from "service/CookieService";
import ErrorHandler from "service/ErrorHandler";
import WebClientService from "service/web/WebClientService";
import F from "utils/F";

export const RetryPolicy = Object.freeze({
    NO_RETRY: {retries: 1, backoff: false},
    RETRY_AND_GIVE_UP: {retries: 3, backoff: false},
    RETRY_WITH_BACKOFF: {retries: 100, backoff: true},
    RETRY_FOREVER: {retries: 1000, backoff: false}
});

export const NotificationPolicy = Object.freeze({
    SHOW: {show: true},
    HIDE: {show: false}
});

export const ReportIssuePolicy = Object.freeze({
    REPORT: {report: true},
    SUPPRESS: {report: false}
});

const DEFAULT_OPTIONS = {
    retryPolicy: RetryPolicy.NO_RETRY,
    notificationPolicy: NotificationPolicy.SHOW,
    reportIssuePolicy: ReportIssuePolicy.REPORT,
    errorHandler: null
};

export default class HttpService {
    static getAsync = async (endpoint, options = {}) => {
        return this._handleRequest(WebClientService.getWebClient().get, endpoint, undefined, options);
    };

    static downloadFile = async (endpoint, fallbackFileName = 'download') => {
        try {
            const api = await this._getApi();
            // Fetch through axios (not an anchor href) so _getConfig()'s Authorization header
            // rides along — a browser navigation can't set it, and the export endpoint is
            // user-scoped, so a raw href arrives as ROLE_GUEST and fails verifyAccess.
            const config = {...this._getConfig(), responseType: 'blob', timeout: 30000};
            const response = await WebClientService.getWebClient().get(`${api}${endpoint}`, config);
            const fileName = this._fileNameFromDisposition(response.headers['content-disposition'], fallbackFileName);
            const objectUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            ErrorHandler.defaultErrorHandler(error, true, true);
        }
    };

    static _fileNameFromDisposition = (disposition, fallback) => {
        if (!disposition) {
            // Cross-origin responses only expose Content-Disposition when the server lists it in
            // Access-Control-Expose-Headers; fall back rather than naming the file "undefined".
            return fallback;
        }
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
        return match ? decodeURIComponent(match[1].trim()) : fallback;
    };

    // data defaults to {} so a body is always present: axios.post/put is (url, data, config), and if
    // we let data be undefined the call collapses to (url, config) — sending our config (with the auth
    // header) as the BODY and dropping authentication. Never let a no-body POST/PUT go out unauthed.
    static postAsync = async (endpoint, data = {}, options = {}) => {
        return this._handleRequest(WebClientService.getWebClient().post, endpoint, data, options);
    };

    static putAsync = async (endpoint, data = {}, options = {}) => {
        return this._handleRequest(WebClientService.getWebClient().put, endpoint, data, options);
    };

    static deleteAsync = async (endpoint, options = {}) => {
        return this._handleRequest(WebClientService.getWebClient().delete, endpoint, undefined, options);
    };

    static _handleRequest = async (webFunction, endpoint, data, options) => {
        const optionsWithDefaults = Object.assign(F.copy(DEFAULT_OPTIONS), options);
        const {retryPolicy, notificationPolicy, reportIssuePolicy} = optionsWithDefaults;
        const config = this._getConfig();
        const api = await this._getApi();
        const url = `${api}${endpoint}`;
        const delay = 2000;
        for (let count = 0; count < retryPolicy.retries; count++) {
            try {
                const asyncWebCall = data !== undefined ? webFunction(url, data, config) : webFunction(url, config);
                const response = await asyncWebCall;
                if (response) {
                    await this._maybeRenewToken(response);
                    return response;
                }
            } catch (error) {
                if (options.errorHandler) {
                    return options.errorHandler(error)
                }
                const showNotification = count === 0 && notificationPolicy.show;
                const reportIssues = reportIssuePolicy.report;
                const canRetry = await ErrorHandler.defaultErrorHandler(error, showNotification, reportIssues);
                if (!canRetry || count === retryPolicy.retries - 1) {
                    // Error already handled (notification shown). Return null so
                    // callers get a falsy result without an unhandled rejection.
                    return null;
                }
            }
            const currentDelay = retryPolicy.backoff ? delay + (Math.pow(count, 2) * 10 + 100) : delay;
            await F.sleep(currentDelay);
        }
    };

    // Sliding-session renewal: the server hands back a freshly-minted token in the Authorization
    // header once the current one is past half its life. Persist it so the active session keeps
    // extending and never hits the hard expiry. Only overwrite when a token is actually present and
    // we still hold one (i.e. an authenticated session), and never let a cookie write break the call.
    static _maybeRenewToken = async (response) => {
        try {
            const renewed = response?.headers?.authorization;
            if (renewed && CookieService.get('token')) {
                await CookieService.set('token', renewed);
            }
        } catch (e) {
            // Best-effort; a failed renewal just means the client keeps using its current token.
        }
    }

    static _getConfig = () => {
        const headers = {};
        const token = CookieService.get('token');
        if (token) {
            headers['Authorization'] = token;
            headers['CompanyId'] = userStore.selectedCompanyId;
        }
        return {
            headers,
            timeout: 10000
        };
    }

    static _getApi = async () => {
        return ConfigService.getConfigAsync('api_url');
    }
}